/* ============================================================================
   Admin module — invite/create-user dialog.
   Collects name + email + role, POSTs /api/manage/users, and surfaces the
   generated temporary password ONCE (it is never retrievable again).
   ============================================================================ */

import { useState } from 'react';
import { apiPost, isApiError } from '../../lib/api';
import Modal from './Modal';
import type { AdminRole, CreateUserResult } from './types';

interface Props {
  roles: AdminRole[];
  canGrantSystemAdmin: boolean;
  onClose: () => void;
  onCreated: () => void;
}

export default function CreateUserDialog({ roles, canGrantSystemAdmin, onClose, onCreated }: Props) {
  const assignable = roles.filter((r) => canGrantSystemAdmin || r.code !== 'SystemAdmin');
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [roleCode, setRoleCode] = useState(
    assignable.find((r) => r.code === 'Requester')?.code ?? assignable[0]?.code ?? '',
  );
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [result, setResult] = useState<CreateUserResult | null>(null);
  const [copied, setCopied] = useState(false);

  async function submit() {
    setError(null);
    if (!name.trim() || !email.trim() || !roleCode) {
      setError('Name, email and role are all required.');
      return;
    }
    setSaving(true);
    try {
      const res = await apiPost<CreateUserResult>('/api/manage/users', {
        name: name.trim(),
        email: email.trim(),
        roleCode,
      });
      setResult(res);
      onCreated(); // refresh the parent list now the user exists
    } catch (e) {
      setError(isApiError(e) ? e.message : 'Could not create the user.');
    } finally {
      setSaving(false);
    }
  }

  function copyPassword() {
    if (!result) return;
    navigator.clipboard?.writeText(result.tempPassword).then(
      () => {
        setCopied(true);
        window.setTimeout(() => setCopied(false), 1500);
      },
      () => undefined,
    );
  }

  // --- Success state: show the temp password once. -------------------------
  if (result) {
    return (
      <Modal
        title="User invited"
        onClose={onClose}
        footer={
          <button className="btn btn-primary" onClick={onClose}>
            Done
          </button>
        }
      >
        <p style={{ marginTop: 0 }}>
          <strong>{result.name}</strong> ({result.email}) was created as{' '}
          <strong>{roles.find((r) => r.code === result.roleCode)?.name ?? result.roleCode}</strong>.
        </p>
        <div className="card" style={{ background: 'var(--paper-2)', padding: 16 }}>
          <p className="eyebrow" style={{ marginTop: 0 }}>
            Temporary password — shown once
          </p>
          <div className="row" style={{ gap: 10, alignItems: 'center' }}>
            <code
              className="mono"
              style={{
                flex: 1,
                fontSize: 'var(--fs-md)',
                padding: '10px 12px',
                background: 'var(--paper)',
                border: '1px solid var(--line-strong)',
                borderRadius: 'var(--r-md)',
                userSelect: 'all',
              }}
            >
              {result.tempPassword}
            </code>
            <button className="btn btn-navy btn-sm" onClick={copyPassword}>
              {copied ? 'Copied' : 'Copy'}
            </button>
          </div>
          <p className="muted" style={{ marginBottom: 0, marginTop: 10 }}>
            Share this with the user securely. It cannot be shown again — you can only issue a new one.
          </p>
        </div>
      </Modal>
    );
  }

  // --- Form state. ---------------------------------------------------------
  return (
    <Modal
      title="Invite a user"
      onClose={onClose}
      footer={
        <>
          <button className="btn btn-ghost" onClick={onClose} disabled={saving}>
            Cancel
          </button>
          <button className="btn btn-primary" onClick={submit} disabled={saving}>
            {saving ? 'Creating…' : 'Create user'}
          </button>
        </>
      }
    >
      {error && (
        <div className="error-text" style={{ marginBottom: 14 }}>
          {error}
        </div>
      )}
      <div className="field">
        <label htmlFor="cu-name">Full name</label>
        <input
          id="cu-name"
          className="in"
          value={name}
          onChange={(e) => setName(e.target.value)}
          placeholder="Jane Doe"
          autoFocus
        />
      </div>
      <div className="field">
        <label htmlFor="cu-email">Email</label>
        <input
          id="cu-email"
          className="in"
          type="email"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          placeholder="jane.doe@uvahealth.org"
        />
      </div>
      <div className="field" style={{ marginBottom: 4 }}>
        <label htmlFor="cu-role">Role</label>
        <select id="cu-role" value={roleCode} onChange={(e) => setRoleCode(e.target.value)}>
          {assignable.map((r) => (
            <option key={r.code} value={r.code}>
              {r.name}
            </option>
          ))}
        </select>
        <span className="hint">
          {roles.find((r) => r.code === roleCode)?.description ??
            'A temporary password is generated automatically.'}
        </span>
      </div>
    </Modal>
  );
}
